import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { Plus, ArrowDown, ArrowUp, Lock, LockOpen, ChartLine, Trash, FilePdf } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { downloadBlob } from '../utils/download';
import { toast } from 'sonner';
import { useConfirm } from '../components/ConfirmProvider';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Espèces' },
  { value: 'card', label: 'Carte bancaire' },
  { value: 'transfer', label: 'Virement' },
  { value: 'check', label: 'Chèque' },
  { value: 'other', label: 'Autre' }
];

const METHOD_LABELS = {
  cash: 'Espèces',
  card: 'Carte',
  transfer: 'Virement',
  check: 'Chèque',
  other: 'Autre'
};

// ============================ Seller Cash Report ============================
const SellerCashReport = ({ isAdmin, currentUsername }) => {
  const [sellers, setSellers] = useState([]);
  const [seller, setSeller] = useState(isAdmin ? '' : currentUsername);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    if (!isAdmin) return; // sellers don't need the list
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/cash/sellers`, { withCredentials: true });
        setSellers(r.data.sellers || []);
        if (r.data.sellers && r.data.sellers.length > 0) setSeller(r.data.sellers[0]);
      } catch (e) {
        toast.error('Erreur chargement vendeurs');
      }
    })();
  }, [isAdmin]);

  const downloadReport = async () => {
    if (!seller) {
      toast.error('Veuillez sélectionner un vendeur');
      return;
    }
    try {
      const params = new URLSearchParams();
      params.append('seller', seller);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate + 'T23:59:59');
      const r = await axios.get(`${API_URL}/api/cash/seller-report/pdf?${params}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      downloadBlob(r.data, `caisse_${seller}.pdf`, 'application/pdf');
      toast.success('Rapport téléchargé');
    } catch (e) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-3">
          {isAdmin ? 'Rapport de caisse par vendeur' : 'Mon rapport de caisse'}
        </h3>
        <p className="text-sm text-blue-800 mb-4">
          {isAdmin
            ? "Sélectionnez un vendeur et une période pour imprimer le détail de ses mouvements de caisse."
            : "Sélectionnez une période pour imprimer le détail de vos mouvements de caisse."}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Vendeur</label>
            {isAdmin ? (
              <select
                value={seller}
                onChange={(e) => setSeller(e.target.value)}
                data-testid="report-seller-select"
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                {sellers.length === 0 && <option value="">Aucun vendeur</option>}
                {sellers.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            ) : (
              <Input value={currentUsername} disabled data-testid="report-seller-locked" />
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Date début</label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="report-start-date" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Date fin</label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="report-end-date" />
          </div>
          <div className="flex items-end">
            <Button
              onClick={downloadReport}
              data-testid="download-seller-report-btn"
              className="w-full bg-red-600 hover:bg-red-500 text-white"
            >
              Télécharger PDF
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CashRegister = () => {
  const confirm = useConfirm();
  const { user } = useAuth();
  const [currentSession, setCurrentSession] = useState(null);
  const [movements, setMovements] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showOpenDialog, setShowOpenDialog] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [showMovementDialog, setShowMovementDialog] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportData, setReportData] = useState(null);

  const [openForm, setOpenForm] = useState({ initial_amount: '', notes: '' });
  const [closeForm, setCloseForm] = useState({ final_amount: '', notes: '' });
  const [movementForm, setMovementForm] = useState({
    type: 'in',
    amount: '',
    payment_method: 'cash',
    category: '',
    description: ''
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchCurrentSession(), fetchSessions(), fetchMovements()]);
    setLoading(false);
  };

  const fetchCurrentSession = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/cash/sessions/current`, { withCredentials: true });
      setCurrentSession(response.data.open ? response.data.session : null);
    } catch (error) {
      console.error('Error fetching current session:', error);
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/cash/sessions`, { withCredentials: true });
      setSessions(response.data);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const fetchMovements = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/cash/movements`, { withCredentials: true });
      setMovements(response.data);
    } catch (error) {
      console.error('Error fetching movements:', error);
    }
  };

  const handleOpenSession = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/cash/sessions/open`, {
        initial_amount: parseFloat(openForm.initial_amount) || 0,
        notes: openForm.notes
      }, { withCredentials: true });
      toast.success('Session ouverte');
      setShowOpenDialog(false);
      setOpenForm({ initial_amount: '', notes: '' });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ouverture');
    }
  };

  const handleCloseSession = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/cash/sessions/close`, {
        final_amount: parseFloat(closeForm.final_amount) || 0,
        notes: closeForm.notes
      }, { withCredentials: true });
      toast.success('Session fermée');
      setShowCloseDialog(false);
      setCloseForm({ final_amount: '', notes: '' });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la fermeture');
    }
  };

  const handleCreateMovement = async (e) => {
    e.preventDefault();
    if (!movementForm.category) {
      toast.error('Veuillez préciser une catégorie');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/cash/movements`, {
        ...movementForm,
        amount: parseFloat(movementForm.amount)
      }, { withCredentials: true });
      toast.success('Mouvement enregistré');
      setShowMovementDialog(false);
      setMovementForm({ type: 'in', amount: '', payment_method: 'cash', category: '', description: '' });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDeleteMovement = async (id) => {
    if (!(await confirm('Supprimer ce mouvement ?'))) return;
    try {
      await axios.delete(`${API_URL}/api/cash/movements/${id}`, { withCredentials: true });
      toast.success('Mouvement supprimé');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const viewReport = async (sessionId) => {
    try {
      const response = await axios.get(`${API_URL}/api/cash/sessions/${sessionId}/report`, { withCredentials: true });
      setReportData(response.data);
      setShowReportDialog(true);
    } catch (error) {
      toast.error('Erreur lors du chargement du rapport');
    }
  };

  const downloadZReport = async (sessionId) => {
    try {
      const response = await axios.get(`${API_URL}/api/cash/sessions/${sessionId}/report/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      downloadBlob(response.data, `z_caisse_${sessionId}.pdf`, 'application/pdf');
      toast.success('Z de caisse téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  if (loading) {
    return <div className="App"><Sidebar /><div className="main-content"><div className="text-center py-12">Chargement...</div></div></div>;
  }

  return (
    <div className="App">
      <Sidebar />
      <div className="main-content" data-testid="cash-register-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Caisse</h1>
            <p className="text-slate-600">Gestion de la caisse et des encaissements</p>
          </div>
          {currentSession ? (
            <Button
              onClick={() => setShowCloseDialog(true)}
              data-testid="close-session-btn"
              className="bg-red-600 hover:bg-red-500 text-white"
            >
              <Lock size={20} weight="bold" className="mr-2" />
              Fermer la session
            </Button>
          ) : (
            <Button
              onClick={() => setShowOpenDialog(true)}
              data-testid="open-session-btn"
              className="bg-orange-500 hover:bg-orange-400 text-white"
            >
              <LockOpen size={20} weight="bold" className="mr-2" />
              Ouvrir une session
            </Button>
          )}
        </div>

        {/* Current Session Card */}
        {currentSession && (
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-6 mb-6" data-testid="current-session-card">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Session en cours</h2>
                <p className="text-sm text-slate-600 mt-1">
                  Ouverte par {currentSession.opened_by} le {new Date(currentSession.opened_at).toLocaleString('fr-FR')}
                </p>
              </div>
              <Button
                onClick={() => setShowMovementDialog(true)}
                data-testid="new-movement-btn"
                className="bg-blue-600 hover:bg-blue-500 text-white"
              >
                <Plus size={18} weight="bold" className="mr-2" />
                Nouveau mouvement
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Fond initial</p>
                <p className="text-2xl font-bold text-slate-900">{currentSession.initial_amount.toFixed(2)} €</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-green-200">
                <p className="text-xs uppercase tracking-wider text-green-700 mb-1">Entrées (espèces)</p>
                <p className="text-2xl font-bold text-green-600">+{currentSession.total_cash_in.toFixed(2)} €</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-red-200">
                <p className="text-xs uppercase tracking-wider text-red-700 mb-1">Sorties (espèces)</p>
                <p className="text-2xl font-bold text-red-600">-{currentSession.total_cash_out.toFixed(2)} €</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-orange-200">
                <p className="text-xs uppercase tracking-wider text-orange-700 mb-1">Solde théorique</p>
                <p className="text-2xl font-bold text-orange-600" data-testid="current-balance">
                  {currentSession.current_balance?.toFixed(2) || '0.00'} €
                </p>
              </div>
            </div>
          </div>
        )}

        {!currentSession && (
          <div className="bg-white border border-slate-200 rounded-lg p-8 text-center mb-6">
            <Lock size={48} className="mx-auto text-slate-400 mb-3" />
            <h3 className="text-lg font-bold text-slate-900 mb-1">Aucune session ouverte</h3>
            <p className="text-sm text-slate-600">Ouvrez une session de caisse pour commencer à enregistrer des mouvements.</p>
          </div>
        )}

        <Tabs defaultValue="movements" className="bg-white border border-slate-200 rounded-lg p-6">
          <TabsList className="grid w-full mb-4 grid-cols-3">
            <TabsTrigger value="movements" data-testid="tab-movements">Mouvements de la session</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">Historique des sessions</TabsTrigger>
            <TabsTrigger value="seller-report" data-testid="tab-seller-report">
              {user?.role === 'admin' ? 'Rapport par vendeur' : 'Mon rapport'}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="movements">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Date</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Type</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Catégorie</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Méthode</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Montant</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Description</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Utilisateur</th>
                    {user?.role === 'admin' && <th className="text-right py-3 px-4 text-xs uppercase text-slate-500"></th>}
                  </tr>
                </thead>
                <tbody>
                  {movements.map((m) => (
                    <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {new Date(m.created_at).toLocaleString('fr-FR')}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <div className={`inline-flex items-center gap-1 font-medium ${m.type === 'in' ? 'text-green-600' : 'text-red-600'}`}>
                          {m.type === 'in' ? <ArrowDown size={14} weight="bold" /> : <ArrowUp size={14} weight="bold" />}
                          {m.type === 'in' ? 'Entrée' : 'Sortie'}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-900 font-medium">{m.category}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{METHOD_LABELS[m.payment_method] || m.payment_method}</td>
                      <td className={`py-3 px-4 text-sm text-right font-bold ${m.type === 'in' ? 'text-green-600' : 'text-red-600'}`}>
                        {m.type === 'in' ? '+' : '-'}{m.amount.toFixed(2)} €
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">{m.description || '-'}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{m.user}</td>
                      {user?.role === 'admin' && (
                        <td className="py-3 px-4 text-right">
                          <button
                            onClick={() => handleDeleteMovement(m.id)}
                            data-testid={`delete-movement-${m.id}`}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash size={16} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {movements.length === 0 && (
                    <tr>
                      <td colSpan="8" className="py-12 text-center text-slate-500">
                        Aucun mouvement enregistré
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="history">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Ouverture</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Fermeture</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Fond initial</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Entrées</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Sorties</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Compté</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Écart</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Statut</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500"></th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s) => (
                    <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {new Date(s.opened_at).toLocaleString('fr-FR')}<br />
                        <span className="text-xs text-slate-500">par {s.opened_by}</span>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {s.closed_at ? (
                          <>
                            {new Date(s.closed_at).toLocaleString('fr-FR')}<br />
                            <span className="text-xs text-slate-500">par {s.closed_by}</span>
                          </>
                        ) : '-'}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-900 text-right">{s.initial_amount.toFixed(2)} €</td>
                      <td className="py-3 px-4 text-sm text-green-600 text-right">+{(s.total_cash_in || 0).toFixed(2)} €</td>
                      <td className="py-3 px-4 text-sm text-red-600 text-right">-{(s.total_cash_out || 0).toFixed(2)} €</td>
                      <td className="py-3 px-4 text-sm text-slate-900 text-right font-medium">
                        {s.final_amount !== null ? `${s.final_amount.toFixed(2)} €` : '-'}
                      </td>
                      <td className={`py-3 px-4 text-sm text-right font-bold ${
                        s.difference === null ? 'text-slate-400' :
                        Math.abs(s.difference) < 0.01 ? 'text-green-600' :
                        s.difference > 0 ? 'text-orange-600' : 'text-red-600'
                      }`}>
                        {s.difference !== null ? `${s.difference >= 0 ? '+' : ''}${s.difference.toFixed(2)} €` : '-'}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                          s.status === 'open' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'
                        }`}>
                          {s.status === 'open' ? 'Ouverte' : 'Fermée'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={() => viewReport(s.id)}
                          data-testid={`view-report-${s.id}`}
                          className="text-blue-600 hover:text-blue-800 mr-2"
                          title="Voir le rapport"
                        >
                          <ChartLine size={18} />
                        </button>
                        <button
                          onClick={() => downloadZReport(s.id)}
                          data-testid={`download-z-report-${s.id}`}
                          className="text-red-600 hover:text-red-800"
                          title="Télécharger Z de caisse (PDF)"
                        >
                          <FilePdf size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {sessions.length === 0 && (
                    <tr>
                      <td colSpan="9" className="py-12 text-center text-slate-500">
                        Aucune session enregistrée
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {user?.role === 'admin' || user ? (
            <TabsContent value="seller-report">
              <SellerCashReport isAdmin={user?.role === 'admin'} currentUsername={user?.username} />
            </TabsContent>
          ) : null}
        </Tabs>

        {/* Open Session Dialog */}
        <Dialog open={showOpenDialog} onOpenChange={setShowOpenDialog}>
          <DialogContent data-testid="open-session-dialog">
            <DialogHeader>
              <DialogTitle>Ouvrir une session de caisse</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleOpenSession} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Fond de caisse initial (€)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={openForm.initial_amount}
                  onChange={(e) => setOpenForm({ ...openForm, initial_amount: e.target.value })}
                  data-testid="open-initial-amount-input"
                  placeholder="0.00"
                  required
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes (optionnel)</label>
                <Input
                  value={openForm.notes}
                  onChange={(e) => setOpenForm({ ...openForm, notes: e.target.value })}
                  data-testid="open-notes-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowOpenDialog(false)}>Annuler</Button>
                <Button type="submit" data-testid="confirm-open-btn" className="bg-orange-500 hover:bg-orange-400 text-white">
                  Ouvrir la session
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Close Session Dialog */}
        <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
          <DialogContent data-testid="close-session-dialog">
            <DialogHeader>
              <DialogTitle>Fermer la session</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCloseSession} className="space-y-4">
              {currentSession && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm">
                  <p className="text-slate-700">Solde théorique attendu :</p>
                  <p className="text-2xl font-bold text-blue-700">{currentSession.current_balance?.toFixed(2) || '0.00'} €</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Montant compté en caisse (€) *</label>
                <Input
                  type="number"
                  step="0.01"
                  value={closeForm.final_amount}
                  onChange={(e) => setCloseForm({ ...closeForm, final_amount: e.target.value })}
                  data-testid="close-final-amount-input"
                  placeholder="0.00"
                  required
                  autoFocus
                />
                <p className="text-xs text-slate-500 mt-1">Comptez physiquement votre caisse et saisissez le montant exact</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes de clôture</label>
                <Input
                  value={closeForm.notes}
                  onChange={(e) => setCloseForm({ ...closeForm, notes: e.target.value })}
                  data-testid="close-notes-input"
                  placeholder="Observations éventuelles..."
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowCloseDialog(false)}>Annuler</Button>
                <Button type="submit" data-testid="confirm-close-btn" className="bg-red-600 hover:bg-red-500 text-white">
                  <Lock size={18} className="mr-2" />
                  Fermer définitivement
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* New Movement Dialog */}
        <Dialog open={showMovementDialog} onOpenChange={setShowMovementDialog}>
          <DialogContent data-testid="movement-dialog">
            <DialogHeader>
              <DialogTitle>Nouveau mouvement de caisse</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateMovement} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setMovementForm({ ...movementForm, type: 'in' })}
                  data-testid="movement-type-in"
                  className={`p-4 rounded-lg border-2 transition-colors duration-200 ${
                    movementForm.type === 'in' ? 'border-green-500 bg-green-50' : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <ArrowDown size={24} weight="bold" className="mx-auto mb-1 text-green-600" />
                  <p className="text-sm font-medium">Entrée</p>
                </button>
                <button
                  type="button"
                  onClick={() => setMovementForm({ ...movementForm, type: 'out' })}
                  data-testid="movement-type-out"
                  className={`p-4 rounded-lg border-2 transition-colors duration-200 ${
                    movementForm.type === 'out' ? 'border-red-500 bg-red-50' : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <ArrowUp size={24} weight="bold" className="mx-auto mb-1 text-red-600" />
                  <p className="text-sm font-medium">Sortie</p>
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Montant (€) *</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={movementForm.amount}
                  onChange={(e) => setMovementForm({ ...movementForm, amount: e.target.value })}
                  data-testid="movement-amount-input"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Méthode de paiement</label>
                <Select
                  value={movementForm.payment_method}
                  onValueChange={(v) => setMovementForm({ ...movementForm, payment_method: v })}
                >
                  <SelectTrigger data-testid="movement-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">Seuls les mouvements en espèces affectent le solde physique de caisse</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Catégorie *</label>
                <Input
                  value={movementForm.category}
                  onChange={(e) => setMovementForm({ ...movementForm, category: e.target.value })}
                  data-testid="movement-category-input"
                  placeholder="Vente, Achat fournisseur, Retrait, etc."
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <Input
                  value={movementForm.description}
                  onChange={(e) => setMovementForm({ ...movementForm, description: e.target.value })}
                  data-testid="movement-description-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowMovementDialog(false)}>Annuler</Button>
                <Button type="submit" data-testid="confirm-movement-btn" className="bg-blue-600 hover:bg-blue-500 text-white">
                  Enregistrer
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Report Dialog */}
        <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="report-dialog">
            <DialogHeader>
              <DialogTitle>Rapport de session</DialogTitle>
            </DialogHeader>
            {reportData && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 p-3 rounded-md">
                    <p className="text-xs uppercase text-slate-500">Ouverture</p>
                    <p className="text-sm text-slate-900 font-medium">
                      {new Date(reportData.session.opened_at).toLocaleString('fr-FR')}
                    </p>
                    <p className="text-xs text-slate-500">par {reportData.session.opened_by}</p>
                  </div>
                  {reportData.session.closed_at && (
                    <div className="bg-slate-50 p-3 rounded-md">
                      <p className="text-xs uppercase text-slate-500">Fermeture</p>
                      <p className="text-sm text-slate-900 font-medium">
                        {new Date(reportData.session.closed_at).toLocaleString('fr-FR')}
                      </p>
                      <p className="text-xs text-slate-500">par {reportData.session.closed_by}</p>
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Totaux par méthode de paiement</h3>
                  <div className="space-y-1">
                    {Object.entries(reportData.totals_by_method).map(([method, total]) => (
                      <div key={method} className="flex justify-between border-b border-slate-100 py-2 text-sm">
                        <span className="text-slate-700">{METHOD_LABELS[method] || method}</span>
                        <span className={`font-bold ${total >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {total >= 0 ? '+' : ''}{total.toFixed(2)} €
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-semibold text-green-700 mb-2">Entrées par catégorie</h3>
                    <div className="space-y-1">
                      {Object.entries(reportData.income_by_category).map(([cat, total]) => (
                        <div key={cat} className="flex justify-between text-sm">
                          <span className="text-slate-700">{cat}</span>
                          <span className="font-medium text-green-600">+{total.toFixed(2)} €</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-red-700 mb-2">Sorties par catégorie</h3>
                    <div className="space-y-1">
                      {Object.entries(reportData.expense_by_category).map(([cat, total]) => (
                        <div key={cat} className="flex justify-between text-sm">
                          <span className="text-slate-700">{cat}</span>
                          <span className="font-medium text-red-600">-{total.toFixed(2)} €</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {reportData.session.status === 'closed' && (
                  <div className="bg-slate-50 p-4 rounded-md">
                    <div className="flex justify-between text-sm mb-1">
                      <span>Solde théorique :</span>
                      <span className="font-medium">{reportData.session.expected_amount?.toFixed(2)} €</span>
                    </div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Montant compté :</span>
                      <span className="font-medium">{reportData.session.final_amount?.toFixed(2)} €</span>
                    </div>
                    <div className="flex justify-between text-base font-bold pt-2 border-t border-slate-200">
                      <span>Écart :</span>
                      <span className={
                        Math.abs(reportData.session.difference) < 0.01 ? 'text-green-600' :
                        reportData.session.difference > 0 ? 'text-orange-600' : 'text-red-600'
                      }>
                        {reportData.session.difference >= 0 ? '+' : ''}{reportData.session.difference?.toFixed(2)} €
                      </span>
                    </div>
                  </div>
                )}

                <div className="flex justify-end pt-2">
                  <Button
                    onClick={() => downloadZReport(reportData.session.id)}
                    data-testid="download-z-report-from-dialog-btn"
                    className="bg-red-600 hover:bg-red-500 text-white"
                  >
                    <FilePdf size={18} weight="bold" className="mr-2" />
                    Télécharger le Z de caisse (PDF)
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CashRegister;
